import React, { useState, useContext, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, NavLink, Redirect, useHistory, useLocation } from 'react-router-dom';
import { SidebarContext } from '../../Context/sidebar';
import { PermissoesSidebarContext } from '../../Context/permissoesSidebar';
import { useNotificacoes } from '../../../hooks/useNotificacoes';
import { fetchMinhasEmpresas, setContextoEmpresaAnalista } from '../../../api/empresas';
import { ensureProtocol } from '../../../config/backend';
import ChatGeminiFlutuante from '../ChatGemini/ChatGeminiFlutuante';
import useTheme from '../../../hooks/useTheme';
import './Sidebar.css';

const ROTAS_POR_GRUPO = {
    cadastros: [
        '/listaUsuario', '/novoUsuario', '/sistemas', '/novoSistema', '/equipamentos', '/novoEquipamento',
        '/cadastro-categorias', '/cadastro-tipos-chamado', '/cadastro-status-chamado', '/cadastro-grupos-usuario',
        '/cadastro-chamados-automaticos', '/base-rag',
    ],
    inventario: ['/listaHardware', '/novoHardware', '/listaHisHar', '/novoVinculo', '/novoDesvinculo', '/novoHisHar', '/hardwaresUsuario', '/hardware'],
    chamados: [
        '/novaSati', '/home', '/satiFechada', '/sprint', '/historicoSati', '/historicoSatiFechada',
    ],
    desenvolvimento: ['/repositorios'],
    worklog: ['/timesheet', '/faturamento', '/tarifas'],
    documentacao: ['/historicoDocumento', '/alterarDocumento'],
    relatorios: ['/relatorioColab', '/relatorioTipo', '/relatorioGeral', '/relatorioChamadosAberto', '/relatorioTempoColaborador', '/relatorioEmpresa', '/paineis'],
    config: ['/configSistema', '/permissoes-usuario', '/config-empresa', '/empresas', '/automacoes', '/webhooks', '/chaves-api', '/politicas-sla', '/guia-configuracao'],
};

function SidebarSubsection({ label, visible, children }) {
    if (!visible) return null;
    return (
        <div className="sidebar-subsection">
            <div className="sidebar-subsection-label">{label}</div>
            <div className="sidebar-subsection-items">{children}</div>
        </div>
    );
}

function rotaPertenceAoGrupo(pathname, prefixos) {
    const path = (pathname || '').split('?')[0];
    return prefixos.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

function grupoDaRota(pathname) {
    return Object.keys(ROTAS_POR_GRUPO).find((grupo) => rotaPertenceAoGrupo(pathname, ROTAS_POR_GRUPO[grupo])) || null;
}

function Sidebar() {
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState({});
    const { collapsed, toggle, setCollapsed } = useContext(SidebarContext);
    const { theme, cycleTheme } = useTheme();
    const themeLabel = { auto: 'Auto', light: 'Claro', dark: 'Escuro' }[theme] || 'Auto';
    const themeIcon = { auto: 'fa-circle-half-stroke', light: 'fa-sun', dark: 'fa-moon' }[theme] || 'fa-circle-half-stroke';
    const { permissoes, loading: loadingPermissoes, refetch: refetchPermissoes } = useContext(PermissoesSidebarContext);
    const pode = (codigo) => {
        if (loadingPermissoes) return false;
        return permissoes == null || permissoes[codigo] !== false;
    };
    const podeAlgum = (...codigos) => codigos.some((c) => pode(c));
    const podeRepositoriosMenu = () => {
        if (loadingPermissoes) return false;
        if (permissoes == null) return true;
        if (permissoes.REPOSITORIOS_LISTA === true) return true;
        if (permissoes.REPOSITORIOS_LISTA === false) return false;
        return permissoes.REPOSITORIOS !== false;
    };
    const mostraGrupoRepositorios = () => {
        if (loadingPermissoes) return false;
        if (permissoes == null) return true;
        return permissoes.REPOSITORIOS !== false || podeRepositoriosMenu();
    };

    const cod = localStorage.getItem('codUsu');
    const history = useHistory();
    const location = useLocation();
    const grupoAtivo = grupoDaRota(location.pathname);
    const { lista: notifLista, countNaoLidas: notificationCount, marcarComoLida } = useNotificacoes();
    const [notifOpen, setNotifOpen] = useState(false);
    const [notifPosition, setNotifPosition] = useState({ top: 0, left: 0 });
    const notifRef = useRef(null);
    const notifDropdownRef = useRef(null);
    const [empresasList, setEmpresasList] = useState([]);
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const [loadingEmpresa, setLoadingEmpresa] = useState(false);
    const switcherRef = useRef(null);
    const empresaIdAtual = localStorage.getItem('empresa_id');
    const empresaNomeAtual = localStorage.getItem('empresa_nome') || '';
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [userMenuPosition, setUserMenuPosition] = useState({ bottom: 0, left: 0 });
    const userMenuRef = useRef(null);
    const userMenuPopoverRef = useRef(null);

    useEffect(() => {
        const close = (e) => {
            const inTrigger = notifRef.current && notifRef.current.contains(e.target);
            const inDropdown = notifDropdownRef.current && notifDropdownRef.current.contains(e.target);
            if (!inTrigger && !inDropdown) setNotifOpen(false);
        };
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, []);

    useEffect(() => {
        if (!notifOpen || !notifRef.current) return;
        const rect = notifRef.current.getBoundingClientRect();
        setNotifPosition({ top: rect.bottom + 4, left: rect.right + 4 });
    }, [notifOpen, collapsed]);

    useEffect(() => {
        document.body.classList.toggle('sidebar-collapsed', collapsed);
        return () => document.body.classList.remove('sidebar-collapsed');
    }, [collapsed]);

    useEffect(() => {
        if (collapsed) {
            setSwitcherOpen(false);
            setUserMenuOpen(false);
        }
    }, [collapsed]);

    useEffect(() => {
        const g = grupoDaRota(location.pathname);
        if (!g) return;
        setExpanded((prev) => (prev[g] ? prev : { ...prev, [g]: true }));
    }, [location.pathname]);

    useEffect(() => {
        const close = (e) => {
            const inTrigger = userMenuRef.current && userMenuRef.current.contains(e.target);
            const inPopover = userMenuPopoverRef.current && userMenuPopoverRef.current.contains(e.target);
            if (!inTrigger && !inPopover) setUserMenuOpen(false);
        };
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, []);

    useEffect(() => {
        if (!userMenuOpen || !userMenuRef.current) return;
        const rect = userMenuRef.current.getBoundingClientRect();
        setUserMenuPosition({
            bottom: window.innerHeight - rect.top + 6,
            left: rect.left,
            width: rect.width,
        });
    }, [userMenuOpen, collapsed]);

    useEffect(() => {
        if (!userMenuOpen) return;
        const onEsc = (e) => { if (e.key === 'Escape') setUserMenuOpen(false); };
        document.addEventListener('keydown', onEsc);
        return () => document.removeEventListener('keydown', onEsc);
    }, [userMenuOpen]);

    useEffect(() => {
        if (!cod || cod === '0') return;
        let cancelled = false;
        fetchMinhasEmpresas()
            .then((arr) => { if (!cancelled) setEmpresasList(Array.isArray(arr) ? arr : []); })
            .catch(() => { if (!cancelled) setEmpresasList([]); });
        return () => { cancelled = true; };
    }, [cod]);

    useEffect(() => {
        const close = (e) => {
            if (switcherRef.current && !switcherRef.current.contains(e.target)) setSwitcherOpen(false);
        };
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, []);

    const trocarEmpresa = async (empId) => {
        if (String(empId) === String(empresaIdAtual)) { setSwitcherOpen(false); return; }
        setLoadingEmpresa(true);
        try {
            const data = await setContextoEmpresaAnalista(Number(empId));
            if (data?.token) {
                localStorage.setItem('token', data.token.startsWith('Bearer ') ? data.token : 'Bearer ' + data.token);
            }
            if (data?.empresa_id != null) localStorage.setItem('empresa_id', data.empresa_id);
            if (data?.empresa_nome != null) localStorage.setItem('empresa_nome', data.empresa_nome);
            if (data?.isSoftbridge != null) localStorage.setItem('isSoftbridge', data.isSoftbridge ? 'S' : 'N');
            if (data?.ia_habilitada != null) localStorage.setItem('ia_habilitada', String(data.ia_habilitada));
            if (data?.per_alterar_solicitante != null) localStorage.setItem('perAltUsu', String(data.per_alterar_solicitante));
            else if (data?.perAltUsu != null) localStorage.setItem('perAltUsu', String(data.perAltUsu));
            setSwitcherOpen(false);
            if (refetchPermissoes) refetchPermissoes();
            window.location.reload();
        } catch (err) {
            console.error('Erro ao trocar empresa:', err);
            setLoadingEmpresa(false);
        }
    };

    const fncSair = () => {

        localStorage.setItem("logado", "N");
        localStorage.setItem("codUsu", 0);
        localStorage.setItem("loginSati", '');
        localStorage.setItem("senhaSati", '');
        localStorage.setItem("nomUsuSat", '');
        localStorage.setItem("usrEmlSat", '');
        localStorage.setItem("tipoSoft", '');
        localStorage.setItem("equpe", '');
        localStorage.setItem("token", '');
        localStorage.setItem("empresa_id", '');
        localStorage.setItem("isSoftbridge", '');
        localStorage.setItem("empresa_nome", '');
        localStorage.setItem("ia_habilitada", '');
    };

    const isSoftbridge = localStorage.getItem("isSoftbridge") === "S";

    const toggleGroup = (key) => {
        if (collapsed) {
            setCollapsed(false);
            setExpanded((prev) => ({ ...prev, [key]: true }));
            return;
        }
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const groupTitleClass = (grupo) => {
        const aberto = !!expanded[grupo];
        const ativo = grupoAtivo === grupo;
        return [
            'sidebar-group-title',
            aberto ? 'sidebar-group-title--open' : '',
            ativo ? 'sidebar-group-title--section-active' : '',
        ].filter(Boolean).join(' ');
    };

    const closeMobile = () => setOpen(false);

    if (cod === '0' || !cod) {
        return <Redirect to='/' />;
    }

    return (
        <>
            <button type="button" className="sidebar-toggle" onClick={() => setOpen(!open)} aria-label="Abrir menu">
                <span className="sidebar-toggle-icon">☰</span>
            </button>
            <div className={`sidebar-overlay ${open ? 'open' : ''}`} onClick={closeMobile} aria-hidden="true" />
            <aside className={`sidebar sidebar-analista ${open ? 'sidebar-open' : ''} ${collapsed ? 'sidebar-collapsed' : ''}`}>
                <div className="sidebar-header">
                    <div className="sidebar-header-top">
                        <Link to="/home" onClick={closeMobile} className="sidebar-logo" title="Início">
                            <span className="sidebar-logo-inner">
                                <img src="/Images/TACOMA_LOGO_NOVA.png" alt="Logo" />
                            </span>
                        </Link>
                        <div className="sidebar-header-actions" ref={notifRef}>
                        <div className="sidebar-notif-wrapper">
                            <button type="button" className="sidebar-notification-btn" aria-label="Notificações" title="Notificações" onClick={() => setNotifOpen(!notifOpen)}>
                                <i className="fas fa-bell" />
                                {notificationCount > 0 && <span className="sidebar-notification-badge">{notificationCount}</span>}
                            </button>
                            {notifOpen && ReactDOM.createPortal(
                                <div ref={notifDropdownRef} className="sidebar-notif-dropdown sidebar-notif-dropdown-portal" style={{ position: 'fixed', top: notifPosition.top, left: notifPosition.left }}>
                                    <div className="sidebar-notif-dropdown-header">Notificações</div>
                                    {notifLista.length === 0 && <div className="sidebar-notif-empty">Nenhuma notificação.</div>}
                                    {notifLista.slice(0, 8).map((n) => (
                                        <button type="button" key={n.id} className={`sidebar-notif-item ${n.lida ? 'lida' : ''}`} onClick={() => { marcarComoLida(n.id); if (n.sat_num) history.push('/historicoSati/' + n.sat_num); setNotifOpen(false); closeMobile(); }}>
                                            <span className="sidebar-notif-titulo">{n.titulo}</span>
                                            {n.mensagem && <span className="sidebar-notif-msg">{n.mensagem}</span>}
                                        </button>
                                    ))}
                                    <Link to="/notificacoes" className="sidebar-notif-ver-todas" onClick={() => { setNotifOpen(false); closeMobile(); }}>Ver todas</Link>
                                </div>,
                                document.body
                            )}
                        </div>
                        <button type="button" className="sidebar-hide-btn sidebar-theme-btn" onClick={cycleTheme} title={`Tema: ${themeLabel} (clique para alternar)`} aria-label={`Alternar tema, atual: ${themeLabel}`}>
                            <i className={`fas ${themeIcon}`} />
                        </button>
                        <button type="button" className="sidebar-hide-btn" onClick={toggle} title={collapsed ? 'Expandir menu' : 'Recolher menu'} aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}>
                            <i className={`fas fa-chevron-${collapsed ? 'right' : 'left'}`} />
                        </button>
                        </div>
                    </div>
                    {(empresaNomeAtual || empresasList.length > 0) && (
                        <div className="sidebar-empresa-switcher" ref={switcherRef}>
                            <button
                                type="button"
                                className="sidebar-empresa-badge sidebar-empresa-badge-btn"
                                onClick={() => setSwitcherOpen(!switcherOpen)}
                                title={empresasList.length > 1 ? 'Trocar empresa' : `Empresa: ${empresaNomeAtual}`}
                                disabled={loadingEmpresa}
                            >
                                <i className="fas fa-building" />
                                <span className="sidebar-empresa-text">{empresaNomeAtual || 'Empresa'}</span>
                                {empresasList.length > 1 && <i className={`fas fa-chevron-${switcherOpen ? 'up' : 'down'} sidebar-empresa-chevron`} />}
                            </button>
                            {switcherOpen && empresasList.length > 1 && (
                                <div className="sidebar-empresa-dropdown">
                                    {empresasList.map((emp) => (
                                        <button
                                            type="button"
                                            key={emp.id}
                                            className={`sidebar-empresa-option ${String(emp.id) === String(empresaIdAtual) ? 'active' : ''}`}
                                            onClick={() => trocarEmpresa(emp.id)}
                                            disabled={loadingEmpresa}
                                        >
                                            <i className="fas fa-building" />
                                            <span>{emp.nome || `Empresa ${emp.id}`}</span>
                                            {String(emp.id) === String(empresaIdAtual) && <i className="fas fa-check" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <nav className="sidebar-nav">
                    {loadingPermissoes ? (
                        <div className="sidebar-nav-loading">
                            <i className="fas fa-spinner fa-spin" aria-hidden />
                            <span>Carregando menu...</span>
                        </div>
                    ) : (
                    <>
                    {pode('DASHBOARD') && (
                    <NavLink to='/dashboard' onClick={closeMobile} className="sidebar-dashboard-link" activeClassName="sidebar-active" title="Dashboard">
                        <i className="fas fa-tachometer-alt" /><span className="sidebar-group-text">Dashboard</span>
                    </NavLink>
                    )}
                    {(pode('CADASTROS') || pode('CADASTROS_USUARIOS') || pode('CADASTROS_SISTEMAS') || pode('CADASTROS_EQUIPAMENTOS') || pode('CADASTROS_CATEGORIAS') || pode('CADASTROS_TIPOS_CHAMADO') || pode('CADASTROS_STATUS_CHAMADO') || pode('CADASTROS_GRUPOS_USUARIO') || pode('CADASTROS_CHAMADOS_AUTOMATICOS') || pode('CADASTROS_BASE_IA')) && (
                    <div className="sidebar-group">
                        <button type="button" className={groupTitleClass('cadastros')} onClick={() => toggleGroup('cadastros')} aria-expanded={!!expanded.cadastros} title="Cadastros">
                            <i className="fas fa-folder-open" /><span className="sidebar-group-text">Cadastros</span>
                            <i className={`fas fa-chevron-down sidebar-group-chevron ${expanded.cadastros ? 'expanded' : ''}`} />
                        </button>
                        <div className={`sidebar-group-items ${expanded.cadastros ? 'expanded' : ''}`}>
                            <div className="sidebar-group-items-inner">
                                <SidebarSubsection label="Pessoas e acesso" visible={podeAlgum('CADASTROS_USUARIOS', 'CADASTROS_GRUPOS_USUARIO')}>
                                    {pode('CADASTROS_USUARIOS') && <NavLink to='/listaUsuario' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-users" /><span>Usuários</span></NavLink>}
                                    {pode('CADASTROS_GRUPOS_USUARIO') && <NavLink to='/cadastro-grupos-usuario' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-user-tag" /><span>Grupos de usuário</span></NavLink>}
                                </SidebarSubsection>
                                <SidebarSubsection label="Fluxo de chamados" visible={podeAlgum('CADASTROS_CATEGORIAS', 'CADASTROS_TIPOS_CHAMADO', 'CADASTROS_STATUS_CHAMADO', 'CADASTROS_CHAMADOS_AUTOMATICOS')}>
                                    {pode('CADASTROS_CATEGORIAS') && <NavLink to='/cadastro-categorias' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-tags" /><span>Categorias</span></NavLink>}
                                    {pode('CADASTROS_TIPOS_CHAMADO') && <NavLink to='/cadastro-tipos-chamado' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-list-alt" /><span>Tipos de chamado</span></NavLink>}
                                    {pode('CADASTROS_STATUS_CHAMADO') && <NavLink to='/cadastro-status-chamado' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-flag" /><span>Status de chamado</span></NavLink>}
                                    {pode('CADASTROS_CHAMADOS_AUTOMATICOS') && <NavLink to='/cadastro-chamados-automaticos' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-bolt" /><span>Chamados automáticos</span></NavLink>}
                                </SidebarSubsection>
                                <SidebarSubsection label="Catálogos" visible={podeAlgum('CADASTROS_SISTEMAS', 'CADASTROS_EQUIPAMENTOS', 'CADASTROS_BASE_IA')}>
                                    {pode('CADASTROS_SISTEMAS') && <NavLink to='/sistemas' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-cubes" /><span>Sistemas</span></NavLink>}
                                    {pode('CADASTROS_EQUIPAMENTOS') && <NavLink to='/equipamentos' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-laptop" /><span>Equipamentos</span></NavLink>}
                                    {pode('CADASTROS_BASE_IA') && <NavLink to='/base-rag' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-database" /><span>Base para IA</span></NavLink>}
                                </SidebarSubsection>
                            </div>
                        </div>
                    </div>
                    )}
                    {(pode('INVENTARIO') || pode('INVENTARIO_HARDWARE') || pode('INVENTARIO_VINCULACAO') || pode('INVENTARIO_DESVINCULAR') || pode('MANUTENCAO_DOCUMENTAR') || pode('MANUTENCAO_HISTORICO')) && (
                    <div className="sidebar-group">
                        <button type="button" className={groupTitleClass('inventario')} onClick={() => toggleGroup('inventario')} aria-expanded={!!expanded.inventario} title="Inventário">
                            <i className="fas fa-boxes" /><span className="sidebar-group-text">Inventário</span>
                            <i className={`fas fa-chevron-down sidebar-group-chevron ${expanded.inventario ? 'expanded' : ''}`} />
                        </button>
                        <div className={`sidebar-group-items ${expanded.inventario ? 'expanded' : ''}`}>
                            <div className="sidebar-group-items-inner">
                                {pode('INVENTARIO_HARDWARE') && <NavLink to='/listaHardware' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-microchip" /><span>Hardwares</span></NavLink>}
                                {pode('MANUTENCAO_HISTORICO') && <NavLink to='/listaHisHar' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-history" /><span>Histórico</span></NavLink>}
                            </div>
                        </div>
                    </div>
                    )}
                    {(pode('CHAMADOS') || podeAlgum('CHAMADOS_NOVO', 'CHAMADOS_ABERTOS', 'CHAMADOS_HISTORICO', 'CHAMADOS_SPRINT')) && (
                    <div className="sidebar-group">
                        <button type="button" className={groupTitleClass('chamados')} onClick={() => toggleGroup('chamados')} aria-expanded={!!expanded.chamados} title="Chamados">
                            <i className="fas fa-ticket-alt" /><span className="sidebar-group-text">Chamados</span>
                            <i className={`fas fa-chevron-down sidebar-group-chevron ${expanded.chamados ? 'expanded' : ''}`} />
                        </button>
                        <div className={`sidebar-group-items ${expanded.chamados ? 'expanded' : ''}`}>
                            <div className="sidebar-group-items-inner">
                                {pode('CHAMADOS_NOVO') && <NavLink to='/novaSati' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-plus-circle" /><span>Chamado</span></NavLink>}
                                {pode('CHAMADOS_ABERTOS') && <NavLink to='/home' onClick={closeMobile} activeClassName="sidebar-active" exact><i className="fas fa-list-ul" /><span>Abertos</span></NavLink>}
                                {pode('CHAMADOS_HISTORICO') && <NavLink to='/satiFechada' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-archive" /><span>Histórico</span></NavLink>}
                                {pode('CHAMADOS_SPRINT') && <NavLink to='/sprint' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-tasks" /><span>Sprint</span></NavLink>}
                            </div>
                        </div>
                    </div>
                    )}
                    {mostraGrupoRepositorios() && (
                    <div className="sidebar-group">
                        <button type="button" className={groupTitleClass('desenvolvimento')} onClick={() => toggleGroup('desenvolvimento')} aria-expanded={!!expanded.desenvolvimento} title="Desenvolvimento">
                            <i className="fas fa-code" /><span className="sidebar-group-text">Desenvolvimento</span>
                            <i className={`fas fa-chevron-down sidebar-group-chevron ${expanded.desenvolvimento ? 'expanded' : ''}`} />
                        </button>
                        <div className={`sidebar-group-items ${expanded.desenvolvimento ? 'expanded' : ''}`}>
                            <div className="sidebar-group-items-inner">
                                {podeRepositoriosMenu() && (
                                    <NavLink to='/repositorios' onClick={closeMobile} activeClassName="sidebar-active" isActive={(_, loc) => (loc.pathname || '').startsWith('/repositorios')}>
                                        <i className="fas fa-folder-open" /><span>Repositórios</span>
                                    </NavLink>
                                )}
                            </div>
                        </div>
                    </div>
                    )}
                    {(pode('WORKLOG') || podeAlgum('WORKLOG_TIMESHEET', 'WORKLOG_FATURAMENTO', 'WORKLOG_TARIFAS')) && (
                    <div className="sidebar-group">
                        <button type="button" className={groupTitleClass('worklog')} onClick={() => toggleGroup('worklog')} aria-expanded={!!expanded.worklog} title="Horas & Faturamento">
                            <i className="fas fa-clock" /><span className="sidebar-group-text">Horas & Faturamento</span>
                            <i className={`fas fa-chevron-down sidebar-group-chevron ${expanded.worklog ? 'expanded' : ''}`} />
                        </button>
                        <div className={`sidebar-group-items ${expanded.worklog ? 'expanded' : ''}`}>
                            <div className="sidebar-group-items-inner">
                                {pode('WORKLOG_TIMESHEET') && <NavLink to='/timesheet' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-calendar-week" /><span>Timesheet</span></NavLink>}
                                {pode('WORKLOG_FATURAMENTO') && <NavLink to='/faturamento' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-file-invoice-dollar" /><span>Faturamento</span></NavLink>}
                                {pode('WORKLOG_TARIFAS') && <NavLink to='/tarifas' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-money-bill-wave" /><span>Tarifas</span></NavLink>}
                            </div>
                        </div>
                    </div>
                    )}
                    {(pode('DOCUMENTACAO') || pode('DOCUMENTACAO_INFRA') || pode('DOCUMENTACAO_SISTEMAS') || pode('DOCUMENTACAO_PROCESSOS')) && (
                    <div className="sidebar-group">
                        <button type="button" className={groupTitleClass('documentacao')} onClick={() => toggleGroup('documentacao')} aria-expanded={!!expanded.documentacao} title="Documentação">
                            <i className="fas fa-book" /><span className="sidebar-group-text">Documentação</span>
                            <i className={`fas fa-chevron-down sidebar-group-chevron ${expanded.documentacao ? 'expanded' : ''}`} />
                        </button>
                        <div className={`sidebar-group-items ${expanded.documentacao ? 'expanded' : ''}`}>
                            <div className="sidebar-group-items-inner">
                                {pode('DOCUMENTACAO_INFRA') && <NavLink to='/historicoDocumento/1' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-server" /><span>Infraestrutura</span></NavLink>}
                                {pode('DOCUMENTACAO_SISTEMAS') && <NavLink to='/historicoDocumento/3' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-book" /><span>Sistemas</span></NavLink>}
                                {pode('DOCUMENTACAO_PROCESSOS') && <NavLink to='/historicoDocumento/0' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-clipboard-list" /><span>Processos e Procedimentos</span></NavLink>}
                            </div>
                        </div>
                    </div>
                    )}
                    {(pode('RELATORIOS') || podeAlgum('RELATORIOS_COLAB', 'RELATORIOS_TIPO', 'RELATORIOS_GERAL', 'RELATORIOS_CHAMADOS_ABERTOS', 'RELATORIOS_TEMPO_COLABORADOR', 'RELATORIOS_EMPRESA', 'RELATORIOS_PAINEIS')) && (
                    <div className="sidebar-group">
                        <button type="button" className={groupTitleClass('relatorios')} onClick={() => toggleGroup('relatorios')} aria-expanded={!!expanded.relatorios} title="Relatórios">
                            <i className="fas fa-chart-bar" /><span className="sidebar-group-text">Relatórios</span>
                            <i className={`fas fa-chevron-down sidebar-group-chevron ${expanded.relatorios ? 'expanded' : ''}`} />
                        </button>
                        <div className={`sidebar-group-items ${expanded.relatorios ? 'expanded' : ''}`}>
                            <div className="sidebar-group-items-inner">
                                <SidebarSubsection label="Gerenciais" visible={podeAlgum('RELATORIOS_COLAB', 'RELATORIOS_TIPO', 'RELATORIOS_GERAL')}>
                                    {pode('RELATORIOS_COLAB') && <NavLink to='/relatorioColab' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-user-tie" /><span>Gerencial Colaborador</span></NavLink>}
                                    {pode('RELATORIOS_TIPO') && <NavLink to='/relatorioTipo' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-chart-pie" /><span>Gerencial Tipo</span></NavLink>}
                                    {pode('RELATORIOS_GERAL') && <NavLink to='/relatorioGeral' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-chart-bar" /><span>Gerencial Total</span></NavLink>}
                                </SidebarSubsection>
                                <SidebarSubsection label="Exportação e análise" visible={podeAlgum('RELATORIOS_CHAMADOS_ABERTOS', 'RELATORIOS_TEMPO_COLABORADOR', 'RELATORIOS_EMPRESA')}>
                                    {pode('RELATORIOS_CHAMADOS_ABERTOS') && <NavLink to='/relatorioChamadosAberto' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-file-excel" /><span>Chamados em aberto (Excel)</span></NavLink>}
                                    {pode('RELATORIOS_TEMPO_COLABORADOR') && <NavLink to='/relatorioTempoColaborador' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-user-clock" /><span>Por colaborador (horas)</span></NavLink>}
                                    {pode('RELATORIOS_EMPRESA') && <NavLink to='/relatorioEmpresa' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-building" /><span>Atendimento por empresa</span></NavLink>}
                                </SidebarSubsection>
                                <SidebarSubsection label="Painéis" visible={pode('RELATORIOS_PAINEIS')}>
                                    {pode('RELATORIOS_PAINEIS') && <NavLink to='/paineis' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-th-large" /><span>Painéis</span></NavLink>}
                                </SidebarSubsection>
                            </div>
                        </div>
                    </div>
                    )}
                    {(pode('CONFIG') || podeAlgum('CONFIG_SISTEMA', 'CONFIG_PERMISSOES_USUARIO', 'CADASTROS_CONFIG_EMPRESA', 'AUTOMACAO') || (isSoftbridge && (String(empresaIdAtual) === '1' || Number(empresaIdAtual) === 1) && pode('ADMINISTRACAO_EMPRESAS'))) && (
                    <div className="sidebar-group">
                        <button type="button" className={groupTitleClass('config')} onClick={() => toggleGroup('config')} aria-expanded={!!expanded.config} title="Config">
                            <i className="fas fa-cog" /><span className="sidebar-group-text">Config</span>
                            <i className={`fas fa-chevron-down sidebar-group-chevron ${expanded.config ? 'expanded' : ''}`} />
                        </button>
                        <div className={`sidebar-group-items ${expanded.config ? 'expanded' : ''}`}>
                            <div className="sidebar-group-items-inner">
                                <SidebarSubsection label="Sistema" visible={podeAlgum('CONFIG_SISTEMA', 'CONFIG_PERMISSOES_USUARIO')}>
                                    {pode('CONFIG_SISTEMA') && <NavLink to='/configSistema' exact onClick={closeMobile} activeClassName="sidebar-active" title="Configurações do sistema"><i className="fas fa-sliders-h" /><span>Configurações do sistema</span></NavLink>}
                                    {pode('CONFIG_PERMISSOES_USUARIO') && <NavLink to='/permissoes-usuario' onClick={closeMobile} activeClassName="sidebar-active" isActive={(_, loc) => (loc.pathname || '').startsWith('/permissoes-usuario')} title="Permissões do menu por usuário"><i className="fas fa-user-lock" /><span>Permissões do menu</span></NavLink>}
                                </SidebarSubsection>
                                <SidebarSubsection label="Empresa" visible={pode('CADASTROS_CONFIG_EMPRESA') || (isSoftbridge && (String(empresaIdAtual) === '1' || Number(empresaIdAtual) === 1) && pode('ADMINISTRACAO_EMPRESAS'))}>
                                    {pode('CADASTROS_CONFIG_EMPRESA') && <NavLink to='/config-empresa' exact onClick={closeMobile} activeClassName="sidebar-active" title="Configuração da empresa"><i className="fas fa-building" /><span>Configuração da empresa</span></NavLink>}
                                    {isSoftbridge && (String(empresaIdAtual) === '1' || Number(empresaIdAtual) === 1) && pode('ADMINISTRACAO_EMPRESAS') && <NavLink to='/empresas' onClick={closeMobile} activeClassName="sidebar-active" title="Nova Empresa"><i className="fas fa-plus-circle" /><span>Nova Empresa</span></NavLink>}
                                </SidebarSubsection>
                                <SidebarSubsection label="Integrações" visible={pode('AUTOMACAO') || pode('WEBHOOKS') || pode('API_CHAVES')}>
                                    {pode('AUTOMACAO') && <NavLink to='/automacoes' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-robot" /><span>Regras de automação</span></NavLink>}
                                    {pode('WEBHOOKS') && <NavLink to='/webhooks' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-bolt" /><span>Webhooks</span></NavLink>}
                                    {pode('API_CHAVES') && <NavLink to='/chaves-api' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-key" /><span>Chaves de API</span></NavLink>}
                                </SidebarSubsection>
                                <SidebarSubsection label="SLA" visible={pode('SLA')}>
                                    <NavLink to='/politicas-sla' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-stopwatch" /><span>Políticas de SLA</span></NavLink>
                                </SidebarSubsection>
                                <SidebarSubsection label="Documentação" visible={pode('CONFIG_SISTEMA')}>
                                    <NavLink to='/guia-configuracao' onClick={closeMobile} activeClassName="sidebar-active"><i className="fas fa-book-open" /><span>Guia de Configuração</span></NavLink>
                                </SidebarSubsection>
                            </div>
                        </div>
                    </div>
                    )}
                    </>
                    )}
                </nav>
                {(() => {
                    const nomeUsu = (localStorage.getItem('nomUsuSat') || '').trim();
                    const displayName = nomeUsu || `Usuário ${cod}`;
                    const inicial = (nomeUsu ? nomeUsu[0] : (cod && cod !== '0' ? cod[0] : 'U')).toUpperCase();
                    const closeMenu = () => setUserMenuOpen(false);
                    return (
                        <div className="sidebar-footer">
                            <button
                                ref={userMenuRef}
                                type="button"
                                className={`sidebar-user-card ${userMenuOpen ? 'is-open' : ''}`}
                                onClick={() => setUserMenuOpen(o => !o)}
                                aria-expanded={userMenuOpen}
                                aria-haspopup="menu"
                                title={displayName}
                            >
                                <span className="sidebar-user-card-avatar" aria-hidden="true">{inicial}</span>
                                <span className="sidebar-user-card-info">
                                    <span className="sidebar-user-card-name">{displayName}</span>
                                    <span className="sidebar-user-card-meta">Online</span>
                                </span>
                                <i className="fas fa-chevron-up sidebar-user-card-chevron" aria-hidden="true" />
                            </button>

                            {userMenuOpen && ReactDOM.createPortal(
                                <div
                                    ref={userMenuPopoverRef}
                                    className="sidebar-user-popover"
                                    role="menu"
                                    style={{
                                        position: 'fixed',
                                        bottom: userMenuPosition.bottom,
                                        left: userMenuPosition.left,
                                        minWidth: Math.max(220, userMenuPosition.width || 0),
                                    }}
                                >
                                    <div className="sidebar-user-popover-header">
                                        <span className="sidebar-user-card-avatar" aria-hidden="true">{inicial}</span>
                                        <div className="sidebar-user-popover-info">
                                            <span className="sidebar-user-popover-name">{displayName}</span>
                                            <span className="sidebar-user-popover-meta">ID {cod}</span>
                                        </div>
                                    </div>
                                    <div className="sidebar-user-popover-separator" />
                                    <NavLink to='/preferenciasAtendimento' onClick={() => { closeMenu(); closeMobile(); }} className="sidebar-user-popover-item" role="menuitem"><i className="fas fa-play-circle" /><span>Preferências</span></NavLink>
                                    <NavLink to='/alterarSenha' onClick={() => { closeMenu(); closeMobile(); }} className="sidebar-user-popover-item" role="menuitem"><i className="fas fa-key" /><span>Alterar senha</span></NavLink>
                                    {pode('NOTIFICACOES') && (
                                        <NavLink to='/notificacoes' onClick={() => { closeMenu(); closeMobile(); }} className="sidebar-user-popover-item" role="menuitem"><i className="fas fa-bell" /><span>Notificações</span></NavLink>
                                    )}
                                    {pode('AJUDA') && (
                                        <NavLink to='/ajuda' onClick={() => { closeMenu(); closeMobile(); }} className="sidebar-user-popover-item" role="menuitem"><i className="fas fa-question-circle" /><span>Ajuda</span></NavLink>
                                    )}
                                    <div className="sidebar-user-popover-separator" />
                                    <Link to='/' onClick={() => { closeMenu(); fncSair(); closeMobile(); }} className="sidebar-user-popover-item sidebar-user-popover-item--danger" role="menuitem"><i className="fas fa-sign-out-alt" /><span>Sair</span></Link>
                                </div>,
                                document.body
                            )}
                        </div>
                    );
                })()}
            </aside>
            {pode('CHAMADOS_ABERTOS') && (
                <ChatGeminiFlutuante
                    apiBaseUrl={ensureProtocol(localStorage.getItem('ipBackend2') || '')}
                    chatPath="/satis/chat-gemini"
                    role="analista"
                />
            )}
        </>
    );
}

export default Sidebar;
